import { isErrorDecryptingParameters, StandardException, encryptPayload, decryptPayload, } from '@standardnotes/encryption';
import { ContentTypeUsesKeySystemRootKeyEncryption, DecryptedPayload, EncryptedPayload, isEncryptedPayload, PayloadEmitSource, SureFindPayload, ContentTypeUsesRootKeyEncryption, } from '@standardnotes/models';
import { AbstractService } from '../Service/AbstractService';
import { ContentType } from '@standardnotes/domain-core';
export class ItemsEncryptionService extends AbstractService {
    constructor(items, payloads, storage, operators, keys, _findDefaultItemsKey, internalEventBus) {
        super(internalEventBus);
        this.items = items;
        this.payloads = payloads;
        this.storage = storage;
        this.operators = operators;
        this.keys = keys;
        this._findDefaultItemsKey = _findDefaultItemsKey;
        this.internalEventBus = internalEventBus;
        this.removeItemsObserver = this.items.addObserver([ContentType.TYPES.ItemsKey], ({ changed, inserted }) => {
            if (changed.concat(inserted).length > 0) {
                void this.decryptErroredItemPayloads();
            }
        });
    }
    deinit() {
        ;
        this.items = undefined;
        this.payloads = undefined;
        this.storage = undefined;
        this.operators = undefined;
        this.keys = undefined;
        this.removeItemsObserver();
        this.removeItemsObserver = undefined;
        super.deinit();
    }
    /**
     * If encryption status changes (esp. on mobile, where local storage encryption
     * can be disabled), consumers may call this function to repersist all items to
     * disk using latest encryption status.
     */
    async repersistAllItems() {
        const items = this.items.items;
        const payloads = items.map((item) => item.payload);
        return this.storage.savePayloads(payloads);
    }
    getItemsKeys() {
        return this.items.getDisplayableItemsKeys();
    }
    itemsKeyForEncryptedPayload(payload) {
        const itemsKeys = this.getItemsKeys();
        const keySystemItemsKeys = this.items.getItems(ContentType.TYPES.KeySystemItemsKey);
        return [...itemsKeys, ...keySystemItemsKeys].find((key) => key.uuid === payload.items_key_id || key.duplicateOf === payload.items_key_id);
    }
    getDefaultItemsKey() {
        return this._findDefaultItemsKey.execute(this.getItemsKeys()).getValue();
    }
    keyToUseForItemEncryption(payload) {
        if (payload.key_system_identifier) {
            const keySystemItemsKey = this.keys.getPrimaryKeySystemItemsKey(payload.key_system_identifier);
            if (!keySystemItemsKey) {
                return new StandardException('Cannot find key system items key to use for encryption');
            }
            return keySystemItemsKey;
        }
        const defaultKey = this.getDefaultItemsKey();
        let result = undefined;
        if (this.userVersion && this.userVersion !== (defaultKey === null || defaultKey === void 0 ? void 0 : defaultKey.keyVersion)) {
            /**
             * The default key appears to be either newer or older than the user's account version
             * We could throw an exception here, but will instead fall back to a corrective action:
             * return any items key that corresponds to the user's version
             */
            const itemsKeys = this.getItemsKeys();
            result = itemsKeys.find((key) => key.keyVersion === this.userVersion);
        }
        else {
            result = defaultKey;
        }
        if (!result) {
            return new StandardException('Cannot find items key to use for encryption');
        }
        return result;
    }
    keyToUseForDecryptionOfPayload(payload) {
        if (payload.items_key_id) {
            const itemsKey = this.itemsKeyForEncryptedPayload(payload);
            return itemsKey;
        }
        const defaultKey = this.defaultItemsKeyForItemVersion(payload.version);
        return defaultKey;
    }
    async encryptPayloadWithKeyLookup(payload, signingKeyPair) {
        const key = this.keyToUseForItemEncryption(payload);
        if (key instanceof StandardException) {
            throw Error(key.message);
        }
        return this.encryptPayload(payload, key, signingKeyPair);
    }
    async encryptPayload(payload, key, signingKeyPair) {
        if (isEncryptedPayload(payload)) {
            throw Error('Attempting to encrypt already encrypted payload.');
        }
        if (!payload.content) {
            throw Error('Attempting to encrypt payload with no content.');
        }
        if (!payload.uuid) {
            throw Error('Attempting to encrypt payload with no UuidGenerator.');
        }
        return encryptPayload(payload, key, this.operators, signingKeyPair);
    }
    async encryptPayloads(payloads, key, signingKeyPair) {
        return Promise.all(payloads.map((payload) => this.encryptPayload(payload, key, signingKeyPair)));
    }
    async encryptPayloadsWithKeyLookup(payloads, signingKeyPair) {
        return Promise.all(payloads.map((payload) => this.encryptPayloadWithKeyLookup(payload, signingKeyPair)));
    }
    async decryptPayloadWithKeyLookup(payload) {
        const key = this.keyToUseForDecryptionOfPayload(payload);
        if (key == undefined) {
            return {
                uuid: payload.uuid,
                errorDecrypting: true,
                waitingForKey: true,
            };
        }
        return this.decryptPayload(payload, key);
    }
    async decryptPayload(payload, key) {
        if (!payload.content) {
            return {
                uuid: payload.uuid,
                errorDecrypting: true,
            };
        }
        return decryptPayload(payload, key, this.operators);
    }
    async decryptPayloadsWithKeyLookup(payloads) {
        return Promise.all(payloads.map((payload) => this.decryptPayloadWithKeyLookup(payload)));
    }
    async decryptPayloads(payloads, key) {
        return Promise.all(payloads.map((payload) => this.decryptPayload(payload, key)));
    }
    async decryptErroredItemPayloads() {
        const erroredItemPayloads = this.payloads.invalidPayloads.filter((i) => !ContentTypeUsesRootKeyEncryption(i.content_type) && !ContentTypeUsesKeySystemRootKeyEncryption(i.content_type));
        if (erroredItemPayloads.length === 0) {
            return;
        }
        const resultParams = await this.decryptPayloadsWithKeyLookup(erroredItemPayloads);
        const decryptedPayloads = resultParams.map((params) => {
            const original = SureFindPayload(erroredItemPayloads, params.uuid);
            if (isErrorDecryptingParameters(params)) {
                return new EncryptedPayload({
                    ...original.ejected(),
                    ...params,
                });
            }
            else {
                return new DecryptedPayload({
                    ...original.ejected(),
                    ...params,
                });
            }
        });
        await this.payloads.emitPayloads(decryptedPayloads, PayloadEmitSource.LocalChanged);
    }
    /**
     * When migrating from non-items key architecture, many items will not have a
     * relationship with any key object. For those items, we can be sure that only 1 key
     * object will correspond to that protocol version.
     * @returns The items key object to decrypt items encrypted
     * with previous protocol version.
     */
    defaultItemsKeyForItemVersion(version, fromKeys) {
        /** Try to find one marked default first */
        const searchKeys = fromKeys || this.getItemsKeys();
        const priorityKey = searchKeys.find((key) => {
            return key.isDefault && key.keyVersion === version;
        });
        if (priorityKey) {
            return priorityKey;
        }
        return searchKeys.find((key) => {
            return key.keyVersion === version;
        });
    }
}
